import { Button, type ButtonProps, IconButton } from "@hortis/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@hortis/ui/command";
import { CommandMobileHeader } from "@hortis/ui/command/command-menu";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
} from "@hortis/ui/dialog";
import { useMediaQuery } from "@hortis/ui/hooks/media-query";
import { ChevronDown, XClose } from "@hortis/ui/icons";
import { Popover, PopoverContent, PopoverTrigger } from "@hortis/ui/popover";
import { CircularProgress } from "@hortis/ui/progress";
import { Tag } from "@hortis/ui/tag";
import { MaterialGroup } from "generated/graphql";
import { type ReactNode, useState } from "react";
import { stopEventPropagation } from "src/features/collection/components/stop-event-propagation";
import { materialGroupLabelMap } from "src/utils/label-maps/material-group";
import { twMerge } from "tailwind-merge";

export const MaterialTypePickerLabel = ({ type }: { type: MaterialGroup }) => (
  <div className="flex items-center gap-2 text-grey-700">
    {materialGroupLabelMap[type]}
  </div>
);

export const MaterialTypePicker = ({
  value,
  onChange,
  placeholder: _placeholder,
  children,
  "data-cy": dataCy,
}: {
  value: MaterialGroup | undefined | null;
  onChange: (val: MaterialGroup | undefined | null) => void;
  children: ReactNode;
  placeholder?: string;
  "data-cy"?: string;
}) => {
  const isDesktop = useMediaQuery("md");
  const placeholder = _placeholder ?? "Select material type...";
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  const handleChange = (type: MaterialGroup) => {
    setSearch("");
    onChange(type);
    setOpen(false);
  };

  const commandContent = (
    <>
      <CommandEmpty className="flex items-center justify-center">
        <CircularProgress size={20} />
      </CommandEmpty>
      <CommandList>
        <CommandGroup>
          <CommandItem
            onSelect={() => {
              handleChange(MaterialGroup.Plant);
            }}
            data-cy="material-type-picker-item"
            selected={value === MaterialGroup.Plant}
          >
            Plant
          </CommandItem>
          <CommandItem
            onSelect={() => {
              handleChange(MaterialGroup.Seed);
            }}
            data-cy="material-type-picker-item"
            selected={value === MaterialGroup.Seed}
          >
            Seed
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </>
  );

  if (isDesktop) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          data-cy={dataCy ?? "material-type-picker"}
          asChild
          onFocusCapture={stopEventPropagation}
        >
          {children}
        </PopoverTrigger>
        <PopoverContent className="z-99999 min-w-(--radix-popover-trigger-width)">
          <Command>
            {" "}
            <CommandInput
              placeholder={placeholder}
              value={search}
              onValueChange={setSearch}
              data-cy="material-type-picker-input"
            />
            {commandContent}
          </Command>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        data-cy={dataCy ?? "material-type-picker"}
        asChild
        onFocusCapture={stopEventPropagation}
      >
        {children}
      </DialogTrigger>
      <DialogContent className="p-0!">
        <Command size="lg">
          <CommandMobileHeader>
            {placeholder}
            <DialogClose asChild>
              <IconButton
                icon={XClose}
                size="xs"
                ariaLabel="Close"
                variant="tertiaryGray"
              />
            </DialogClose>
          </CommandMobileHeader>
          {commandContent}
        </Command>
      </DialogContent>
    </Dialog>
  );
};

export const MaterialTypeMultiPicker = ({
  value,
  onChange,
  placeholder: _placeholder,
  ...props
}: {
  value: Array<MaterialGroup> | undefined | null;
  onChange: (val: Array<MaterialGroup> | undefined | null) => void;
  placeholder?: string;
} & Partial<Omit<ButtonProps, "value" | "onChange">>) => {
  const placeholder = _placeholder ?? "Select a type...";
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  const handleChange = (status: MaterialGroup) => {
    onChange(
      value?.includes(status) === true
        ? value.filter((v) => v !== status)
        : [...(value ?? []), status],
    );
    setSearch("");
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          className={twMerge(
            "min-h-[44px] w-full justify-between truncate px-[14px]! py-2! text-grey-500",
          )}
          endIcon={ChevronDown}
          data-cy="material-type-picker"
          {...props}
        >
          {value == null || value.length === 0 ? (
            placeholder
          ) : (
            <div className="flex flex-wrap items-center gap-2 truncate">
              {value.map((type) => (
                <Tag key={type} data-cy="material-type-picker-tag">
                  {materialGroupLabelMap[type]}
                </Tag>
              ))}
            </div>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="z-99999 w-(--radix-popover-trigger-width)!">
        <Command>
          <CommandInput
            placeholder={placeholder}
            value={search}
            onValueChange={setSearch}
            data-cy="material-type-picker-input"
          />
          <CommandEmpty className="flex items-center justify-center">
            <CircularProgress size={20} />
          </CommandEmpty>
          <CommandList>
            <CommandGroup>
              <CommandItem
                onSelect={() => {
                  handleChange(MaterialGroup.Plant);
                }}
                data-cy="material-type-picker-item"
                selected={value?.includes(MaterialGroup.Plant)}
              >
                Plant
              </CommandItem>
              <CommandItem
                onSelect={() => {
                  handleChange(MaterialGroup.Seed);
                }}
                data-cy="material-type-picker-item"
                selected={value?.includes(MaterialGroup.Seed)}
              >
                Seed
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
